import { type FeedPage, getPosts } from "../../queries.ts";
import { RpcClient } from "../api.ts";
import { viewTransport } from "../auth.ts";
import { type ThreadPreview, threadPreviews } from "../feed-threads.ts";
import { filterChanges, isFiltered } from "../filters.ts";
import { postPath } from "../router.ts";
import { rkeyOf } from "../repo.ts";
import { PagedList } from "./paged-list.tsx";
import { actorLabel } from "./actor.tsx";
import { PostCard, PostResultView } from "./post.tsx";

export function Feed(
  loadPage: (cursor?: string) => Promise<FeedPage>,
  signal: AbortSignal,
  showMuted = false,
  context?: "threads" | "parents",
): Element {
  const seen = new Set<string>();
  return PagedList(
    async (cursor) => {
      const page = await loadPage(cursor);
      const items = context
        ? await threadPreviews(
          page.feed.filter((item) => !isFiltered(item.post, showMuted)),
          seen,
          async (uris) => {
            const result = await new RpcClient(viewTransport(getPosts.id)).call(getPosts, {
              parameters: { uris: [...uris] },
              signal,
            });
            return result.posts;
          },
          context,
        )
        : page.feed.map((item) => ({ key: item.uri, item, ancestors: [], gap: false }));
      return {
        items,
        ...(page.cursor && { cursor: page.cursor }),
        ...(page.backfilling &&
          { notice: "Your timeline is still being backfilled. Refresh to see more posts." }),
      };
    },
    (preview) => preview.key,
    (preview) => {
      const row = FeedRow(preview, signal, showMuted);
      if (row) seen.add(preview.key);
      return row;
    },
    signal,
    "No posts yet.",
  );
}

function FeedRow(
  { item, ancestors, gap }: ThreadPreview,
  signal: AbortSignal,
  showMuted: boolean,
): Element | undefined {
  if (isFiltered(item.post, showMuted)) return undefined;
  const context = <div class="thread-ancestors"></div>;
  if (ancestors.length) {
    for (const [index, result] of ancestors.entries()) {
      if (gap && index === 1) {
        context.append(
          <a
            class="thread-gap"
            href={postPath(item.post.author, rkeyOf(item.post.uri))}
            dataset={{ route: "" }}
          >
            View full thread
          </a>,
        );
      }
      const ancestor = PostResultView({
        result,
        signal,
        showMuted: showMuted && result.$type === "blue.cerulea.app.views#availablePost" &&
          result.post.author.did === item.post.author.did,
        variant: "thread",
      });
      if (ancestor) context.append(ancestor);
    }
  }
  const card = PostCard({
    post: item.post,
    signal,
    showMuted,
    variant: context.childElementCount ? "thread" : "feed",
  });
  if (context.childElementCount) {
    const thread = (
      <section class="feed-thread" aria-label="Thread preview">{context}{card}</section>
    );
    filterChanges.addEventListener("change", () => {
      if (isFiltered(item.post, showMuted)) thread.remove();
    }, { signal });
    return thread;
  }
  const by = item.repostedBy;
  if (!by) return card;
  return (
    <div class="feed-row">
      <div class="repost-label">Reposted by {actorLabel(by)}</div>
      {card}
    </div>
  );
}
